import React from 'react';
import { useLanguage } from '../../hooks/useLanguage';

const ModuleTrendsChart = ({ data, loading, days = 30 }) => {
    const { t } = useLanguage();
    if (loading) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <h5 className="card-title mb-4">{t('metrics.trends.title')} ({t('metrics.trends.lastDays', { days })})</h5>
                    <div className="placeholder-glow">
                        <div className="placeholder col-12" style={{height: '300px'}}></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <h5 className="card-title mb-4">{t('metrics.trends.title')} ({t('metrics.trends.lastDays', { days })})</h5>
                    <div className="text-center text-muted py-5">
                        <i className="bi bi-graph-up" style={{fontSize: '3rem'}}></i>
                        <p className="mt-3">{t('metrics.trends.noTrendsData')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Calcular el valor máximo para escalar
    const maxValue = Math.max(
        ...data.map(d => Math.max(d.policy_navigator || 0, d.journal_finder || 0, d.concursos || 0))
    );
    const chartHeight = 250;
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };

    // Calcular posiciones de puntos
    const calculatePoints = (dataArray, key) => {
        return dataArray.map((d, index) => {
            const x = (index / (dataArray.length - 1)) * 100;
            const y = chartHeight - ((d[key] / maxValue) * (chartHeight - padding.top - padding.bottom));
            return { x, y, value: d[key], fecha: d.fecha };
        });
    };

    const policyPoints = calculatePoints(data, 'policy_navigator');
    const journalPoints = calculatePoints(data, 'journal_finder');
    const concursosPoints = calculatePoints(data, 'concursos');

    // Crear path para líneas
    const createPath = (points) => {
        if (points.length === 0) return '';
        const pathData = points.map((p, i) => 
            `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
        ).join(' ');
        return pathData;
    };

    // Crear path para área bajo la línea
    const createAreaPath = (points) => {
        if (points.length === 0) return '';
        const pathData = points.map((p, i) => 
            `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
        ).join(' ');
        return `${pathData} L ${points[points.length - 1].x},${chartHeight} L 0,${chartHeight} Z`;
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h5 className="card-title mb-4">
                    <i className="bi bi-graph-up-arrow me-2 text-primary"></i>
                    {t('metrics.trends.title')} ({t('metrics.trends.lastDays', { days })})
                </h5>

                <div style={{ position: 'relative', width: '100%', height: `${chartHeight}px`, marginBottom: '20px' }}>
                    <svg 
                        viewBox={`0 0 100 ${chartHeight}`} 
                        style={{ width: '100%', height: '100%' }}
                        preserveAspectRatio="none"
                    >
                        {/* Líneas de cuadrícula */}
                        {[0, 25, 50, 75, 100].map((percent) => {
                            const y = chartHeight - (percent / 100) * (chartHeight - padding.top - padding.bottom);
                            return (
                                <g key={percent}>
                                    <line
                                        x1="0"
                                        y1={y}
                                        x2="100"
                                        y2={y}
                                        stroke="#e5e7eb"
                                        strokeWidth="0.2"
                                        strokeDasharray="1,1"
                                    />
                                    <text
                                        x="-2"
                                        y={y}
                                        fontSize="3"
                                        fill="#6b7280"
                                        textAnchor="end"
                                        dominantBaseline="middle"
                                    >
                                        {Math.round((maxValue * percent) / 100)}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Área bajo Policy Navigator */}
                        <path
                            d={createAreaPath(policyPoints)}
                            fill="rgba(59, 130, 246, 0.1)"
                        />

                        {/* Área bajo Journal Finder */}
                        <path
                            d={createAreaPath(journalPoints)}
                            fill="rgba(16, 185, 129, 0.1)"
                        />

                        {/* Área bajo Concursos */}
                        <path
                            d={createAreaPath(concursosPoints)}
                            fill="rgba(245, 158, 11, 0.1)"
                        />

                        {/* Línea Policy Navigator */}
                        <path
                            d={createPath(policyPoints)}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="0.5"
                            strokeLinejoin="round"
                        />

                        {/* Línea Journal Finder */}
                        <path
                            d={createPath(journalPoints)}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="0.5"
                            strokeLinejoin="round"
                        />

                        {/* Línea Concursos */}
                        <path
                            d={createPath(concursosPoints)}
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth="0.5"
                            strokeLinejoin="round"
                        />

                        {/* Puntos Policy Navigator */}
                        {policyPoints.map((point, i) => (
                            <circle
                                key={`policy-${i}`}
                                cx={point.x}
                                cy={point.y}
                                r="0.8"
                                fill="#3b82f6"
                                className="chart-point"
                            >
                                <title>{`${point.fecha}: ${point.value} ${t('metrics.kpi.queries')}`}</title>
                            </circle>
                        ))}

                        {/* Puntos Journal Finder */}
                        {journalPoints.map((point, i) => (
                            <circle
                                key={`journal-${i}`}
                                cx={point.x}
                                cy={point.y}
                                r="0.8"
                                fill="#10b981"
                                className="chart-point"
                            >
                                <title>{`${point.fecha}: ${point.value} ${t('metrics.kpi.queries')}`}</title>
                            </circle>
                        ))}

                        {/* Puntos Concursos */}
                        {concursosPoints.map((point, i) => (
                            <circle
                                key={`concursos-${i}`}
                                cx={point.x}
                                cy={point.y}
                                r="0.8"
                                fill="#f59e0b"
                                className="chart-point"
                            >
                                <title>{`${point.fecha}: ${point.value} ${t('metrics.kpi.queries')}`}</title>
                            </circle>
                        ))}
                    </svg>
                </div>

                {/* Leyenda */}
                <div className="d-flex justify-content-center gap-4 mt-3">
                    <div className="d-flex align-items-center">
                        <div style={{
                            width: '20px',
                            height: '3px',
                            backgroundColor: '#3b82f6',
                            marginRight: '8px'
                        }}></div>
                        <span className="small">{t('metrics.moduleUsage.legend.policyNavigator')}</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <div style={{
                            width: '20px',
                            height: '3px',
                            backgroundColor: '#10b981',
                            marginRight: '8px'
                        }}></div>
                        <span className="small">{t('metrics.moduleUsage.legend.journalFinder')}</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <div style={{
                            width: '20px',
                            height: '3px',
                            backgroundColor: '#f59e0b',
                            marginRight: '8px'
                        }}></div>
                        <span className="small">{t('metrics.moduleUsage.legend.concursos')}</span>
                    </div>
                </div>

                {/* Información de fechas */}
                <div className="text-center mt-3">
                    <small className="text-muted">
                        <i className="bi bi-calendar-range me-1"></i>
                        {data.length > 0 && (
                            <>
                                {data[0].fecha} — {data[data.length - 1].fecha}
                            </>
                        )}
                    </small>
                </div>
            </div>

            <style>{`
                .chart-point {
                    cursor: pointer;
                    transition: r 0.2s;
                }
                .chart-point:hover {
                    r: 1.5 !important;
                }
            `}</style>
        </div>
    );
};

export default ModuleTrendsChart;
